// The character, drawn in pen, layer by layer: cape, legs, boots, body armour, shield arm,
// head and headgear, weapon arm. Every worn item changes what is drawn, so two players who
// found different gear look different on the page.
import { inkLine, inkPath, inkEllipse, hatch, penText, pencil, FONT_PEN, INK, PENCIL, RED, SKIN, SKIN_LINE, W } from './ink.ts';
import type { EntityState } from '../../../packages/protocol/src/index.ts';
import type { EquipState } from '../../../packages/protocol/src/index.ts';
import { ITEMS } from '../../../packages/game-data/src/index.ts';
import type { Look } from '../../../packages/game-data/src/index.ts';
import { pipBar, hashStr } from './art.ts';
import { bake, blit, spriteScale } from './sprite.ts';

const lookOf = (id: string | undefined): Look | null => (id ? ITEMS[id]?.look ?? null : null);

// How impressive a piece of gear is allowed to look. This is the whole point of the wardrobe: a
// relic breastplate has to read as a relic across the room, not as a tunic in a different colour.
/** what the cloth or metal of a piece of gear is filled with, so armour does not read as paper */
function fillOf(look: Look | null, tier = 0): string {
  if (!look) return 'rgba(255,255,255,0.4)';
  switch (look.kind) {
    case 'plate': return tier >= 4 ? 'rgba(206,212,222,0.95)' : 'rgba(190,194,200,0.92)';
    case 'leather': return 'rgba(166,124,74,0.85)';
    case 'robe': return (look.color ?? '#2c2c44') + '30';
    case 'tunic': return (look.color ?? '#3d55a0') + '4a';
    case 'helm': return tier >= 4 ? 'rgba(206,212,222,0.97)' : 'rgba(186,190,196,0.97)';
    case 'hood': return 'rgba(236,233,226,0.95)';
    case 'cap': return 'rgba(250,248,242,0.95)';
    default: return look.color ? look.color + '3a' : 'rgba(255,255,255,0.55)';
  }
}

/** a boot is a solid object, not an outline: it gets a real colour under the line */
function bootFill(boots: Look | null): string {
  if (!boots) return 'rgba(74,74,74,0.85)';
  const c = boots.color ?? '#3a3a3a';
  return c + 'd8';
}

const C_WOOD = '#9a7549';

const TIER: Record<string, number> = { common: 1, uncommon: 2, rare: 3, relic: 4, mythic: 5 };
const tierOf = (id: string | undefined): number => (id ? TIER[ITEMS[id]?.rarity ?? 'common'] ?? 1 : 0);

// The character is small on the page, so it is drawn with a heavier hand than the scenery: one
// confident weight around the silhouette, a lighter one inside it. Thin scratchy outlines are what
// make a figure read as a sketch instead of as a drawing somebody finished.
const HW = { silhouette: 2.4, edge: 1.15, detail: 0.8, shade: 0.55 };

// Figure proportions, in px, feet at the origin. Two builds of the same character: the stubby one
// the game started with, and a taller one with a smaller head and longer limbs. Total height is
// about the same in both, so the world scale does not change — what changes is how the height is
// divided up, which is the whole difference between "doodle" and "hero".
export type Build = 'chibi' | 'heroic';
const BUILDS: Record<Build, typeof H> = {
  chibi: {
    head: -60, headR: 8.4,
    neck: -51, shoulder: -48, chest: -40, waist: -31, hip: -27,
    torsoW: 30, hipW: 16, knee: -13, foot: 0,
    armLen: 21, handY: -29,
  },
  heroic: {
    head: -64, headR: 5.4,
    neck: -58, shoulder: -56, chest: -45, waist: -34, hip: -30,
    torsoW: 26, hipW: 12.5, knee: -15, foot: 0,
    armLen: 27, handY: -32,
  },
};
const H = {
  head: -60, headR: 8.4,
  neck: -51, shoulder: -48, chest: -40, waist: -31, hip: -27,
  torsoW: 30, hipW: 16, knee: -13, foot: 0,
  armLen: 21, handY: -29,
};
let build: Build = 'heroic';
Object.assign(H, BUILDS.heroic);
export const currentBuild = () => build;
export function setBuild(b: Build) {
  build = b;
  Object.assign(H, BUILDS[b]);
}

export function drawHero(
  ctx: CanvasRenderingContext2D, e: EntityState, t: number, self: boolean, swing: number, eq: EquipState,
) {
  const seed = hashStr(e.id);
  const ink = e.ink ?? INK;
  const moving = e.v > 8;
  const view: 'front' | 'back' | 'side' = Math.sin(e.a) > 0.4 ? 'front' : Math.sin(e.a) < -0.4 ? 'back' : 'side';
  const flip = Math.cos(e.a) < 0 ? -1 : 1;
  // the animation is a flipbook: eight steps of the walk, four of the swing, one standing still
  const phase = moving ? Math.floor((((t * 9 + seed) % (Math.PI * 2)) / (Math.PI * 2)) * 8) : 8;
  const sw = swing > 0.02 ? Math.min(3, Math.floor(swing * 4)) : -1;
  const sig = [ink, eq.weapon, eq.offhand, eq.head, eq.chest, eq.boots, eq.cape].join(',');
  const box = heroFrameBox(eq);
  const baked = bake(`hero|${build}|${sig}|${view}|${phase}|${sw}`, box, spriteScale(), (c) => {
    const step = phase === 8 ? 0 : Math.sin((phase / 8) * Math.PI * 2);
    const bob = phase === 8 ? 0 : Math.abs(Math.cos((phase / 8) * Math.PI * 2)) * 2.6;
    drawFigure(c, { ink, eq, view, step, bob, swing: sw < 0 ? 0 : (sw + 1) / 4 });
  });

  ctx.save();
  ctx.translate(e.x, e.y);
  if (e.dead) { ctx.rotate(1.35); ctx.globalAlpha = 0.5; }
  shadow(ctx, seed);
  if (baked) {
    if (view === 'side' && flip < 0) { ctx.scale(-1, 1); blit(ctx, baked, 0, 0); }
    else blit(ctx, baked, 0, 0);
  }
  ctx.restore();

  // your own figure is underlined in red pen, the way you would mark your own drawing
  if (self) inkLine(ctx, e.x - 17, e.y + 3, e.x + 17, e.y + 3, { color: RED, width: 1.8, amp: 1.2, seed: seed + 9, alpha: 0.5 });
  penText(ctx, e.name ?? '', e.x, e.y - 108, { size: 13, align: 'center', color: self ? RED : INK, seed: seed + 10 });
  if (e.hp !== undefined && e.hpMax && e.hp < e.hpMax && !e.dead) pipBar(ctx, e.x - 18, e.y - 102, 36, e.hp / e.hpMax, seed);
}

/** one frame of the figure, drawn in local units with the feet at the origin */
function drawFigure(ctx: CanvasRenderingContext2D, o: { ink: string; eq: EquipState; view: 'front' | 'back' | 'side'; step: number; bob: number; swing: number }) {
  const { ink, eq, view, step, bob, swing } = o;
  const seed = 7;
  // Standing still, the weight goes onto one leg and the body leans off the vertical. A figure
  // that stands perfectly straight and symmetrical reads as a mannequin, not as a person.
  const idle = step === 0 && swing === 0;
  const stance = idle ? (view === 'side' ? 0.16 : 0.34) : step;
  ctx.save();
  ctx.scale(1.24, 1.24);
  ctx.translate(0, -bob);
  if (idle) { ctx.translate(0.7, 0); ctx.rotate(-0.035); }
  const chest = lookOf(eq.chest), boots = lookOf(eq.boots), cape = lookOf(eq.cape);
  const head = lookOf(eq.head), weapon = lookOf(eq.weapon), shield = lookOf(eq.offhand);
  const tier = {
    chest: tierOf(eq.chest), head: tierOf(eq.head), weapon: tierOf(eq.weapon),
    offhand: tierOf(eq.offhand), cape: tierOf(eq.cape), boots: tierOf(eq.boots),
  };
  if (cape) drawCape(ctx, cape, seed, step, view, tier.cape);
  drawLegs(ctx, ink, chest, boots, seed, stance, tier.boots);
  drawTorso(ctx, ink, chest, seed, view, tier.chest);
  const bow = weapon?.kind === 'bow';
  const twoHanded = bow || weapon?.kind === 'staff';
  // a shield you cannot hold is slung across the back, so it is always visible on the character
  if (shield && (twoHanded || view === 'side')) drawShieldOnBack(ctx, shield, seed);
  if (view !== 'side' && !bow) drawArm(ctx, ink, chest, -1, seed, idle ? -0.3 : step, 0, tier.chest);
  if (shield && view !== 'side' && !twoHanded) drawShield(ctx, shield, seed, -1, false, tier.offhand, idle ? -0.3 : step);
  drawHead(ctx, ink, head, seed, view, false, tier.head);
  if (bow) drawBowPose(ctx, ink, chest, weapon!, swing, tier.chest);
  else {
    drawArm(ctx, ink, chest, 1, seed, idle ? 0.25 : step, swing, tier.chest);
    if (weapon) drawWeapon(ctx, weapon, seed, swing, view, tier.weapon, idle);
  }
  ctx.restore();
}

// The village people are drawn by the same machinery, with fixed gear, so they belong to the
// same world as the players instead of being simpler doodles.
const NPC_GEAR: Record<string, EquipState> = {
  npc_margit: { chest: 'scribe_robe', head: 'ink_hood', weapon: 'ink_staff' },
  npc_vendor: { chest: 'pupil_tunic', head: 'paper_cap' },
};
/** QA: draw one figure with the given gear into a fresh canvas and report how much ink it used */
/** one figure with the given gear, drawn where the caller puts it: for art sheets and the audit */
export function drawFigureFor(ctx: CanvasRenderingContext2D, eq: EquipState, view: 'front' | 'back' | 'side' = 'front', ink = '#1b2f6b') {
  drawFigure(ctx, { ink, eq, view, step: 0, bob: 0, swing: 0 });
}

/** one numbered frame of the walk cycle, for looking at the animation as a strip */
export function drawWalkFrame(ctx: CanvasRenderingContext2D, eq: EquipState, phase: number, view: 'front' | 'back' | 'side' = 'front', swing = 0) {
  const step = swing > 0 ? 0 : Math.sin((phase / 8) * Math.PI * 2);
  const bob = swing > 0 ? 0 : Math.abs(Math.cos((phase / 8) * Math.PI * 2)) * 2.6;
  drawFigure(ctx, { ink: '#1b2f6b', eq, view, step, bob, swing });
}

/** The baked-frame box for a figure, in world units with the feet at (0,0). It must hold the
 *  weapon at the top of its swing, not just at rest: a swung blade reaches out to the right and a
 *  staff (len 64) sweeps to about x=143 and towers above the head. When this box was too small the
 *  weapon tip was silently cut off at the edge of its baked canvas during an attack. Only a staff
 *  pays for the taller, wider frame; every other weapon fits the standard box.
 *  `scripts/check-hero-clip.mjs` asserts figureBounds() never exceeds what this returns. */
export function heroFrameBox(eq: EquipState): { x: number; y: number; w: number; h: number } {
  const staff = ITEMS[eq.weapon ?? '']?.look?.kind === 'staff';
  return staff ? { x: -76, y: -160, w: 226, h: 172 } : { x: -76, y: -120, w: 192, h: 132 };
}

/** the tightest box the figure actually draws inside, used to size the baked frames */
export function figureBounds(eq: EquipState): { x: number; y: number; w: number; h: number } | null {
  // The sampling canvas must be wider than any pose reaches, or it clips the very extent it is
  // meant to measure: a swung staff reaches x≈143 and up to y≈-152.
  const S = 3, bx = -90, by = -176, bw = 260, bh = 192;
  let minX = 1e9, minY = 1e9, maxX = -1e9, maxY = -1e9;
  for (const view of ['front', 'back', 'side'] as const) {
    for (const swing of [0, 0.5, 1]) {
      for (const step of [-1, 0, 1]) {
        const cv = document.createElement('canvas');
        cv.width = bw * S; cv.height = bh * S;
        const c = cv.getContext('2d');
        if (!c) return null;
        c.scale(S, S); c.translate(-bx, -by);
        drawFigure(c, { ink: '#1b2f6b', eq, view, step, bob: 1.8, swing });
        const d = c.getImageData(0, 0, cv.width, cv.height).data;
        for (let y = 0; y < cv.height; y++) for (let x = 0; x < cv.width; x++) {
          if (d[(y * cv.width + x) * 4 + 3]! > 8) {
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          }
        }
      }
    }
  }
  if (minX > maxX) return null;
  return { x: bx + minX / S, y: by + minY / S, w: (maxX - minX) / S, h: (maxY - minY) / S };
}

export function figureInk(eq: EquipState, view: 'front' | 'back' | 'side' = 'front'): { pixels: number; sig: number; w: number; h: number } {
  const cv = document.createElement('canvas');
  const S = 3;
  cv.width = 124 * S; cv.height = 138 * S;
  const c = cv.getContext('2d');
  if (!c) return { pixels: 0, sig: 0, w: 0, h: 0 };
  c.scale(S, S); c.translate(62, 126);
  drawFigure(c, { ink: '#1b2f6b', eq, view, step: 0, bob: 0, swing: 0 });
  const data = c.getImageData(0, 0, cv.width, cv.height).data;
  // count the ink, and also fingerprint the colour, so an item that only changes the palette
  // still counts as visible
  let n = 0, sig = 0;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3]!;
    if (a <= 12) continue;
    n++;
    sig = (sig + ((data[i]! >> 4) * 97 + (data[i + 1]! >> 4) * 89 + (data[i + 2]! >> 4) * 83) * ((i / 4) % 719)) % 2147483647;
  }
  return { pixels: n, sig, w: cv.width, h: cv.height };
}

export function drawNpcFigure(ctx: CanvasRenderingContext2D, e: EntityState, t: number, hasQuest: boolean) {
  const gear = NPC_GEAR[e.def] ?? { chest: 'pupil_tunic' };
  drawHero(ctx, { ...e, ink: '#3a4a7a', v: 0, a: Math.PI / 2 }, t, false, 0, gear);
  if (hasQuest) drawQuestMark(ctx, e.x, e.y, t);
}

/** the red circled exclamation a teacher would put in the margin */
export function drawQuestMark(ctx: CanvasRenderingContext2D, x: number, y: number, t: number) {
  // circled in red pen above the name, the way you would ring something on a page to come back to
  const qy = y - 152 + Math.sin(t * 2.6) * 3;
  inkEllipse(ctx, x, qy, 13, 16, { color: RED, width: 2.2, amp: 1.7, seed: 991, alpha: 0.95, overshoot: 0.9 });
  // the mark itself, drawn rather than typed, so it has the weight of the rest of the page
  inkPath(ctx, [[x - 1.6, qy - 9], [x + 1.6, qy - 9], [x + 1, qy + 2], [x - 1, qy + 2]],
    { color: RED, width: 1.4, seed: 992, amp: 0.5, close: true, fill: 'rgba(200,48,47,0.85)' });
  ctx.save(); ctx.fillStyle = RED;
  ctx.beginPath(); ctx.arc(x, qy + 6.5, 1.9, 0, 7); ctx.fill(); ctx.restore();
}

function shadow(ctx: CanvasRenderingContext2D, seed: number) {
  for (let i = 0; i < 3; i++) {
    inkLine(ctx, -12 + i, i * 1.6 - 1, 12 - i, i * 1.6 - 1, { color: PENCIL, width: 1.1, amp: 1.3, alpha: 0.2, seed: seed + i });
  }
}

// ---------- legs ----------
function drawLegs(ctx: CanvasRenderingContext2D, ink: string, chest: Look | null, boots: Look | null, seed: number, step: number, tier = 0) {
  void tier;
  const robe = chest?.kind === 'robe';
  const trouser = chest?.kind === 'plate' ? '#4a4a4a' : chest?.kind === 'leather' ? '#5a4326' : ink;
  if (robe) {
    const c = chest!.color ?? ink, ac = chest!.accent ?? c;
    const sway = step * 2.2;
    // a robe hangs in folds and flares at the hem
    inkPath(ctx, [[-10, H.hip], [-12.5 + sway * 0.6, H.knee], [-16 + sway, -3], [16 + sway, -3], [12.5 + sway * 0.6, H.knee], [10, H.hip]],
      { color: c, width: HW.silhouette * 0.9, seed, amp: 0.8, close: true, fill: fillOf(chest, tier) });
    // the light down the near side, and the shadow on the other
    ctx.save(); ctx.globalAlpha = 0.4; ctx.fillStyle = '#ffffff';
    ctx.fillRect(-14 + sway, H.hip, 8, H.hip * -1 - 3); ctx.restore();
    // folds, each one a line that widens towards the hem
    for (const fx of [-8.5, -4, 0.5, 5, 9]) {
      inkPath(ctx, [[fx * 0.5, H.hip + 1], [fx * 0.8 + sway * 0.5, H.knee], [fx * 1.1 + sway, -4]],
        { color: c, width: HW.shade, seed: seed + fx + 20, amp: 0.5, alpha: 0.5 });
    }
    // the hem band and a line of stitching above it
    inkPath(ctx, [[-16 + sway, -3], [16 + sway, -3], [15.4 + sway, -7], [-15.4 + sway, -7]],
      { color: ac, width: HW.detail, seed: seed + 3, amp: 0.6, close: true, fill: ac + '55' });
    for (let i = 0; i < 9; i++) inkLine(ctx, -14 + sway + i * 3.5, -6.4, -13.2 + sway + i * 3.5, -3.6, { color: ac, width: HW.shade, amp: 0.25, alpha: 0.6, seed: seed + 60 + i });
    // a sash knotted at the waist
    inkPath(ctx, [[-10.5, H.waist - 2], [10.5, H.waist - 2], [10, H.waist + 3], [-10, H.waist + 3]],
      { color: ac, width: HW.detail, seed: seed + 5, amp: 0.4, close: true, fill: ac + '66' });
    inkPath(ctx, [[3, H.waist + 2], [5.5, H.waist + 11], [2.5, H.waist + 10]], { color: ac, width: HW.shade, seed: seed + 6, amp: 0.5, alpha: 0.85, close: true, fill: ac + '55' });
    hatch(ctx, 3, H.hip, 12, 22, { gap: 4, angle: -1.2, alpha: 0.16, seed: seed + 4 });
    return;
  }
  if (chest?.kind === 'plate') {
    const ac2 = chest.accent ?? ink, mat2 = fillOf(chest, tier);
    for (const s of [-1, 1] as const) {
      inkPath(ctx, [[s * 1, H.hip - 3], [s * (H.hipW / 2 + 3), H.hip - 3], [s * (H.hipW / 2 + 2), H.hip + 7], [s * 1.5, H.hip + 7]],
        { color: ac2, width: W.detail, seed: seed + s * 51, amp: 0.4, close: true, fill: mat2 });
      inkLine(ctx, s * 1.5, H.hip + 1.5, s * (H.hipW / 2 + 2.5), H.hip + 1.5, { color: ac2, width: W.shade, amp: 0.3, alpha: 0.8, seed: seed + s * 53 });
    }
  }
  for (const s of [-1, 1] as const) {
    const swingX = (s === 1 ? step : -step) * 7.5;
    const hipX = s * 5;
    // the leading leg bends at the knee, the trailing one straightens
    const bend = Math.max(0, (s === 1 ? step : -step)) * 2.2;
    const kneeX = hipX + swingX * 0.55 + bend, ankleX = hipX + swingX;
    // thigh, then shin, with a knee where they meet
    const legMat = fillOf(chest, tier);
    const thighY = (H.hip + H.knee) / 2, calfY = H.knee / 2 - 2;
    inkPath(ctx, [[hipX - 4.6, H.hip], [hipX + 4.6, H.hip], [hipX + 4.2 + (kneeX - hipX) * 0.5, thighY], [kneeX + 3.5, H.knee], [kneeX - 3.5, H.knee], [hipX - 4.2 + (kneeX - hipX) * 0.5, thighY]],
      { color: trouser, width: HW.silhouette * 0.85, seed: seed + s * 7, amp: 0.45, close: true, fill: legMat });
    inkPath(ctx, [[kneeX - 3.5, H.knee], [kneeX + 3.5, H.knee], [kneeX + 4 + (ankleX - kneeX) * 0.4, calfY], [ankleX + 3, -5], [ankleX - 3, -5], [kneeX - 3.2 + (ankleX - kneeX) * 0.4, calfY]],
      { color: trouser, width: HW.silhouette * 0.85, seed: seed + s * 9, amp: 0.45, close: true, fill: legMat });
    // a knee cop over the joint on real armour
    if (chest?.kind === 'plate') {
      inkEllipse(ctx, kneeX, H.knee + 0.5, 3.6, 2.8, { color: chest.accent ?? trouser, width: W.detail, seed: seed + s * 41, amp: 0.35, fill: legMat });
    }
    inkLine(ctx, kneeX - 2.4, H.knee, kneeX + 2.4, H.knee + 0.6, { color: trouser, width: W.shade, amp: 0.4, alpha: 0.6, seed: seed + s * 11 });
    hatch(ctx, kneeX + 0.6, H.knee, 2.6, 8, { gap: 2.6, angle: -1.3, alpha: 0.2, seed: seed + s * 13 });
    // boot: cuff, foot and a sole line
    const bc = boots?.color ?? '#3a3a3a';
    const tall = boots?.shape === 'kite' ? 5 : 0;          // courier boots come up the shin
    const toe = boots?.shape === 'round' ? 1.8 : 0;        // eraser boots have a fat rounded toe
    const knit = boots?.shape === 'square';                // woollen boots slouch and are knitted
    inkPath(ctx, [[ankleX - 3.8, -7.5 - tall], [ankleX + 3.8, -7.5 - tall], [ankleX + 3.6, -3.4], [ankleX + 5.4 + toe, -2.8], [ankleX + 6.2 + toe, -1], [ankleX + 5.8 + toe, 0],
      [ankleX - 3.6, 0], [ankleX - 4.4, -1.2], [ankleX - 4, -3]],
      { color: bc, width: HW.silhouette * 0.82, seed: seed + s * 15, amp: 0.45, close: true, fill: bootFill(boots) });
    // the light along the top of the foot, and a heel under the back
    ctx.save(); ctx.globalAlpha = 0.3; ctx.fillStyle = '#ffffff';
    ctx.fillRect(ankleX - 3.4, -6.6 - tall, 2.4, 4.4); ctx.restore();
    inkPath(ctx, [[ankleX - 4.2, -2.4], [ankleX - 1.6, -2.4], [ankleX - 1.8, 0], [ankleX - 3.8, 0]],
      { color: bc, width: HW.shade, seed: seed + s * 45, amp: 0.3, close: true, alpha: 0.85, fill: 'rgba(0,0,0,0.25)' });
    if (boots) inkLine(ctx, ankleX - 3.6, -6.4 - tall, ankleX + 3.5, -6.4 - tall, { color: boots.accent ?? bc, width: W.detail, amp: 0.4, alpha: 0.9, seed: seed + s * 17 });
    if (knit) {
      // a slouched woollen cuff and a few rows of knit
      inkPath(ctx, [[ankleX - 4.2, -6.8], [ankleX - 2, -10.5], [ankleX + 2, -10], [ankleX + 4.2, -6.4], [ankleX + 3.6, -5.2], [ankleX - 3.8, -5.6]],
        { color: boots!.accent ?? bc, width: W.detail, seed: seed + s * 61, amp: 0.45, close: true, fill: 'rgba(216,207,232,0.9)' });
      for (let i = 0; i < 3; i++) inkLine(ctx, ankleX - 3.2 + i * 0.4, -9 + i * 1.4, ankleX + 3.2 - i * 0.4, -8.6 + i * 1.4, { color: bc, width: W.shade, amp: 0.3, alpha: 0.6, seed: seed + s * 63 + i });
      for (let i = 0; i < 4; i++) inkLine(ctx, ankleX - 2.6 + i * 1.7, -4.4, ankleX - 2.2 + i * 1.7, -1.8, { color: bc, width: W.shade, amp: 0.25, alpha: 0.5, seed: seed + s * 67 + i });
    }
    if (tall) inkLine(ctx, ankleX - 3.4, -5, ankleX + 3.3, -5, { color: boots?.accent ?? bc, width: W.shade, amp: 0.3, alpha: 0.8, seed: seed + s * 23 });
    // a soft boot rolls over at the top
    if (boots && !tall && !toe && !knit) inkPath(ctx, [[ankleX - 3.8, -7.4], [ankleX, -9.2], [ankleX + 3.8, -7.4], [ankleX + 3.4, -6], [ankleX - 3.4, -6]],
      { color: boots.accent ?? bc, width: W.shade, seed: seed + s * 29, amp: 0.35, close: true, alpha: 0.9 });
    inkLine(ctx, ankleX - 4.1, -0.6, ankleX + 5.2, -0.6, { color: bc, width: W.shade, amp: 0.3, alpha: 0.7, seed: seed + s * 19 });
  }
}

// ---------- torso ----------
function drawTorso(ctx: CanvasRenderingContext2D, ink: string, chest: Look | null, seed: number, view: string, tier = 0) {
  const c = chest?.color ?? ink;
  const ac = chest?.accent ?? c;
  const w = H.torsoW / 2;
  const plate = chest?.kind === 'plate';
  // A V from the shoulders into the waist. The shoulders are the widest thing on the figure and
  // the waist the narrowest: that contrast is most of what reads as "strong" at this size.
  const body: [number, number][] = [
    [-w, H.shoulder + 2], [-w * 0.7, H.shoulder - 3], [0, H.shoulder - 4], [w * 0.7, H.shoulder - 3], [w, H.shoulder + 2],
    [w * 0.9, H.chest], [H.hipW / 2 + 1.5, H.waist], [H.hipW / 2, H.hip], [-H.hipW / 2, H.hip], [-H.hipW / 2 - 1.5, H.waist], [-w * 0.9, H.chest],
  ];
  inkPath(ctx, body, { color: c, width: HW.silhouette, seed, amp: 0.6, close: true, fill: fillOf(chest, tier) });
  // the light catches the left of the chest
  ctx.save();
  ctx.beginPath(); body.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip();
  ctx.globalAlpha = plate ? 0.4 : 0.22; ctx.fillStyle = '#ffffff';
  ctx.fillRect(-w, H.shoulder - 4, w * 0.62, H.hip - H.shoulder + 4);
  ctx.restore();

  if (plate) {
    // breastplate: a centre ridge, banded plates, rivets
    inkLine(ctx, 0, H.shoulder + 1, 0, H.waist - 1, { color: ac, width: W.detail, amp: 0.4, alpha: 0.9, seed: seed + 6 });
    for (const y of [H.chest + 1, H.chest + 7]) inkPath(ctx, [[-w * 0.82, y], [0, y + 1.8], [w * 0.82, y]], { color: ac, width: W.shade, seed: seed + y, amp: 0.4, alpha: 0.7 });
    // a gorget over the collarbones on anything better than plain plate
    if (tier >= 3) {
      inkPath(ctx, [[-w * 0.62, H.shoulder + 1], [0, H.shoulder + 5.5], [w * 0.62, H.shoulder + 1], [w * 0.5, H.shoulder + 4], [0, H.shoulder + 8], [-w * 0.5, H.shoulder + 4]],
        { color: ac, width: W.detail, seed: seed + 41, amp: 0.4, close: true, fill: 'rgba(255,255,255,0.5)' });
    }
    for (const sx of [-w * 0.55, w * 0.55]) { ctx.fillStyle = ac; ctx.beginPath(); ctx.arc(sx, H.chest + 3.5, 1, 0, 7); ctx.fill(); }
  } else if (chest?.kind === 'leather') {
    inkPath(ctx, [[-w + 2, H.shoulder + 3], [0, H.chest + 2], [w - 3, H.hip + 2]], { color: ac, width: W.edge, seed: seed + 7, amp: 0.5 });
    inkPath(ctx, [[-w + 2.5, H.chest + 4], [w - 2.5, H.chest + 4]], { color: ac, width: W.detail, seed: seed + 8, amp: 0.4, alpha: 0.8 });
    for (let i = 0; i < 7; i++) {
      const sx = -w + 3 + i * ((2 * w - 6) / 7);
      ctx.fillStyle = ac; ctx.beginPath(); ctx.arc(sx, H.chest + 4, 0.85, 0, 7); ctx.fill();
    }
  } else if (chest?.kind === 'robe') {
    inkPath(ctx, [[-5, H.shoulder + 1], [-1.8, H.chest + 2], [-1.8, H.hip]], { color: ac, width: W.detail, seed: seed + 9, amp: 0.4 });
    inkPath(ctx, [[5, H.shoulder + 1], [1.8, H.chest + 2], [1.8, H.hip]], { color: ac, width: W.detail, seed: seed + 10, amp: 0.4 });
  } else if (chest) {
    // a tunic: a laced neckline, a stitched hem band, side seams and a couple of folds
    inkPath(ctx, [[-5.5, H.shoulder - 2], [0, H.shoulder + 4.5], [5.5, H.shoulder - 2]], { color: ac, width: HW.detail, seed: seed + 11, amp: 0.4 });
    inkPath(ctx, [[-6.4, H.shoulder - 1], [0, H.shoulder + 6], [6.4, H.shoulder - 1]], { color: ac, width: HW.shade, seed: seed + 18, amp: 0.4, alpha: 0.85 });
    for (let i = 0; i < 3; i++) {
      const ly = H.shoulder + 1 + i * 1.9;
      inkLine(ctx, -1.9 + i * 0.35, ly, 1.9 - i * 0.35, ly + 0.8, { color: ac, width: HW.shade, amp: 0.25, alpha: 0.8, seed: seed + 70 + i });
    }
    // the hem, a band of its own
    inkPath(ctx, [[-H.hipW / 2 - 2.5, H.hip - 3.5], [H.hipW / 2 + 2.5, H.hip - 3.5], [H.hipW / 2 + 2, H.hip], [-H.hipW / 2 - 2, H.hip]],
      { color: ac, width: HW.detail, seed: seed + 19, amp: 0.35, close: true, fill: ac + '55' });
    for (let i = 0; i < 5; i++) inkLine(ctx, -6 + i * 3, H.hip - 3, -5.4 + i * 3, H.hip - 0.6, { color: ac, width: HW.shade, amp: 0.25, alpha: 0.6, seed: seed + 80 + i });
    // side seams and two folds in the cloth
    for (const s3 of [-1, 1] as const) inkPath(ctx, [[s3 * (w - 1.5), H.shoulder + 3], [s3 * (w - 2.5), H.chest + 2], [s3 * (H.hipW / 2 + 1), H.hip - 4]],
      { color: ac, width: HW.shade, seed: seed + s3 * 23, amp: 0.4, alpha: 0.6 });
    inkPath(ctx, [[-w * 0.4, H.chest + 3], [-w * 0.22, H.hip - 5]], { color: c, width: HW.shade, seed: seed + 12, amp: 0.4, alpha: 0.5 });
    inkPath(ctx, [[w * 0.38, H.chest + 4], [w * 0.2, H.hip - 5]], { color: c, width: HW.shade, seed: seed + 13, amp: 0.4, alpha: 0.5 });
  } else {
    inkPath(ctx, [[-5, H.shoulder - 1], [0, H.shoulder + 3.5], [5, H.shoulder - 1]], { color: c, width: W.detail, seed: seed + 11, amp: 0.4 });
  }

  // Pauldrons. These are the loudest thing on an armoured character and the clearest signal that
  // the gear got better, so they are drawn big: a relic pauldron is about as wide as the chest and
  // stands proud of the shoulder line, with real spikes on it.
  if (tier >= 2 && chest?.kind !== 'robe') {
    const g = tier - 1;                                   // 1 at uncommon, 4 at mythic
    const out = 4 + g * 3.2;                              // how far it reaches past the shoulder
    const up = 2 + g * 1.6;                               // how far it stands above the shoulder
    const drop = H.chest + 2 + g * 1.6;                   // how far down the arm it hangs
    const mat = fillOf(chest, tier);
    for (const s2 of [-1, 1] as const) {
      const ox = s2 * w;
      // the cap itself, a dome over the joint
      const cap: [number, number][] = [
        [ox - s2 * 3, H.shoulder + 1],
        [ox - s2 * 1, H.shoulder - up],
        [ox + s2 * (out * 0.45), H.shoulder - up * 1.15],
        [ox + s2 * out, H.shoulder + 1.5],
        [ox + s2 * (out * 0.92), drop],
        [ox - s2 * 3.5, drop - 1.5],
      ];
      pencil(ctx, cap, { color: c, width: HW.silhouette * 0.85, seed: seed + s2 * 5, amp: 0.5, smooth: true, fill: mat, fillAlpha: 1 });
      // a second, lower lame on real armour, so it reads as plates and not as a blob
      if (tier >= 3) {
        inkPath(ctx, [[ox - s2 * 2.5, drop - 3], [ox + s2 * (out * 0.9), drop - 4], [ox + s2 * (out * 0.8), drop + 4], [ox - s2 * 2, drop + 3]],
          { color: c, width: HW.detail, seed: seed + s2 * 9, amp: 0.4, close: true, fill: mat });
      }
      // the ridge along the top and the rivet at the joint
      inkPath(ctx, [[ox - s2 * 1, H.shoulder - up + 1], [ox + s2 * (out * 0.5), H.shoulder - up * 1.05 + 1.5], [ox + s2 * (out * 0.9), H.shoulder + 2.5]],
        { color: ac, width: HW.shade, seed: seed + s2 * 6, amp: 0.3, alpha: 0.9 });
      ctx.fillStyle = ac; ctx.beginPath(); ctx.arc(ox - s2 * 1.5, H.shoulder + 2.5, 1 + 0.3 * g, 0, 7); ctx.fill();
      // the light catches the top of the plate
      ctx.save(); ctx.globalAlpha = 0.45; ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.ellipse(ox + s2 * out * 0.35, H.shoulder - up * 0.4, out * 0.3, up * 0.55, s2 * 0.3, 0, 7); ctx.fill(); ctx.restore();
      // spikes: one at rare, three at relic and above
      if (tier >= 3) {
        // Steel with a bright tip, not solid gold: gold spikes on the shoulders read as a second
        // crown and fight with the one on the head.
        const spikes = tier >= 4 ? 2 : 1;
        for (let i = 0; i < spikes; i++) {
          const f = spikes === 1 ? 0.52 : 0.34 + i * 0.34;
          const bx = ox + s2 * out * f;
          const by = H.shoulder - up * (0.82 + Math.sin(f * Math.PI) * 0.3);
          const len2 = 4.5 + g * 1.1 + Math.sin(f * Math.PI) * 2;
          inkPath(ctx, [[bx - s2 * 2, by + 1], [bx + s2 * (len2 * 0.3), by - len2], [bx + s2 * 2.4, by + 1.5]],
            { color: c, width: HW.detail, seed: seed + s2 * (11 + i), amp: 0.3, close: true, fill: mat });
          inkPath(ctx, [[bx + s2 * (len2 * 0.3) - s2 * 0.9, by - len2 * 0.62], [bx + s2 * (len2 * 0.3), by - len2], [bx + s2 * (len2 * 0.3) + s2 * 0.9, by - len2 * 0.6]],
            { color: ac, width: HW.shade, seed: seed + s2 * (21 + i), amp: 0.25, close: true, fill: ac + 'cc' });
        }
      }
    }
  }

  // A stone at the throat, set into the collar. Small, but it is the one bright point on a figure
  // otherwise made of steel, and the eye goes straight to it.
  if (tier >= 3 && chest?.kind !== 'robe') {
    const gy = H.shoulder + 5.5;
    inkPath(ctx, [[-4.5, gy - 3], [4.5, gy - 3], [3.5, gy + 1.5], [-3.5, gy + 1.5]],
      { color: ac, width: HW.shade, seed: seed + 66, amp: 0.3, close: true, fill: ac + '99' });
    const gem = tier >= 5 ? '#4fd0e0' : tier >= 4 ? '#c8302f' : '#3d55a0';
    inkPath(ctx, [[0, gy - 1], [2.6, gy + 1.6], [0, gy + 5.4], [-2.6, gy + 1.6]],
      { color: '#2a2a2a', width: HW.shade, seed: seed + 67, amp: 0.25, close: true, fill: gem });
    ctx.save(); ctx.globalAlpha = 0.8; ctx.fillStyle = '#ffffff';
    ctx.beginPath(); ctx.moveTo(-0.9, gy + 0.9); ctx.lineTo(0.2, gy + 0.2); ctx.lineTo(0.2, gy + 2.4); ctx.closePath(); ctx.fill();
    ctx.restore();
    // relics glow: a few short rays off the stone
    if (tier >= 4) for (let i = 0; i < 6; i++) {
      const a2 = (i / 6) * Math.PI * 2 + 0.4;
      inkLine(ctx, Math.cos(a2) * 4.5, gy + 2 + Math.sin(a2) * 4.5, Math.cos(a2) * 7.5, gy + 2 + Math.sin(a2) * 7.5,
        { color: gem, width: HW.shade, amp: 0.25, alpha: 0.45, seed: seed + 70 + i });
    }
  }

  // belt with a buckle and a short strap end
  if (chest?.kind !== 'robe') {
    const beltC = plate ? '#6a5a3a' : '#5a4326';
    inkPath(ctx, [[-H.hipW / 2 - 2, H.waist - 1.8], [H.hipW / 2 + 2, H.waist - 1.8], [H.hipW / 2 + 2, H.waist + 2], [-H.hipW / 2 - 2, H.waist + 2]],
      { color: beltC, width: W.detail, seed: seed + 14, amp: 0.3, close: true, fill: 'rgba(90,67,38,0.4)' });
    ctx.fillStyle = tier >= 3 ? '#e8c24a' : '#d8a34a';
    ctx.fillRect(-2.1, H.waist - 2, 4.2, 4);
    inkLine(ctx, 2.4, H.waist + 1.4, 5, H.waist + 3.6, { color: beltC, width: W.shade, amp: 0.3, seed: seed + 15 });
  }
  // shading down the shaded side and under the chest
  hatch(ctx, w * 0.34, H.shoulder + 2, w * 0.72, H.hip - H.shoulder - 3, { gap: 3.2, angle: -1.15, alpha: 0.17, seed: seed + 16 });
  hatch(ctx, -w * 0.5, H.waist - 3.5, w, 3, { gap: 3, angle: -1.15, alpha: 0.1, seed: seed + 17 });
  void view;
}

// ---------- arms ----------
function drawArm(ctx: CanvasRenderingContext2D, ink: string, chest: Look | null, side: -1 | 1, seed: number, step: number, swing: number, tier = 0) {
  const c = chest?.color ?? ink;
  const sx = side * (H.torsoW / 2 - 1.5);
  const a0 = H.shoulder + 2;
  const lift = side === 1 ? armLift(swing) : 0;
  const armA = side * (0.55 + step * 0.42 * side) + lift;
  // upper arm to the elbow, then the forearm, so the limb bends like a limb
  const ex = sx + Math.sin(armA) * (H.armLen * 0.55), ey = a0 + Math.cos(armA) * (H.armLen * 0.55);
  const armB = armA + side * 0.25 - lift * 0.35 + step * 0.16 * side;
  const hx = ex + Math.sin(armB) * (H.armLen * 0.5), hy = ey + Math.cos(armB) * (H.armLen * 0.5);
  const mat = fillOf(chest, tier);
  inkPath(ctx, [[sx - 3.1, a0 - 1.5], [sx + 3.1, a0 - 1.5], [ex + 2.6, ey], [ex - 2.6, ey]], { color: c, width: HW.silhouette * 0.8, seed: seed + side * 17, amp: 0.45, close: true, fill: mat });
  inkPath(ctx, [[ex - 2.6, ey - 0.5], [ex + 2.6, ey - 0.5], [hx + 2.3, hy], [hx - 2.3, hy]], { color: c, width: HW.silhouette * 0.8, seed: seed + side * 19, amp: 0.45, close: true, fill: mat });
  // a bracer over the forearm once the armour is worth the name
  if (tier >= 3 && chest?.kind !== 'robe') {
    const mx = (ex + hx) / 2, my = (ey + hy) / 2;
    inkPath(ctx, [[mx - 2.8, my - 2.5], [mx + 2.8, my - 2.5], [mx + 2.5, my + 3], [mx - 2.5, my + 3]],
      { color: chest?.accent ?? c, width: W.detail, seed: seed + side * 31, amp: 0.35, close: true, fill: mat });
    inkLine(ctx, mx - 2.4, my, mx + 2.4, my, { color: chest?.accent ?? c, width: W.shade, amp: 0.25, alpha: 0.8, seed: seed + side * 33 });
  }
  // cuff at the wrist
  inkLine(ctx, hx - 2.3, hy - 1.2, hx + 2.3, hy - 1.2, { color: chest?.accent ?? c, width: W.shade, amp: 0.3, alpha: 0.8, seed: seed + side * 21 });
  drawHand(ctx, hx, hy + 1.8, chest, tier, seed + side * 23);
  hatch(ctx, Math.min(sx, ex) + (side > 0 ? 1.2 : -3), a0, 3, H.armLen * 0.7, { gap: 2.6, angle: -1.3, alpha: 0.14, seed: seed + side * 29 });
  return { x: hx, y: hy + 1.8 };
}

/** the arm rises through the wind-up and drives down through the cut */
function armLift(swing: number): number {
  if (swing <= 0) return 0;
  const up = Math.min(1, swing / 0.35);
  const down = Math.max(0, (swing - 0.35) / 0.65);
  return -up * 2.4 + down * 3.2;
}

function handPos(side: -1 | 1, step: number, swing: number) {
  const sx = side * (H.torsoW / 2 - 1.5);
  const a0 = H.shoulder + 2;
  const lift = side === 1 ? armLift(swing) : 0;
  const armA = side * (0.55 + step * 0.42 * side) + lift;
  const ex = sx + Math.sin(armA) * (H.armLen * 0.55), ey = a0 + Math.cos(armA) * (H.armLen * 0.55);
  const armB = armA + side * 0.25 - lift * 0.35 + step * 0.16 * side;
  return { x: ex + Math.sin(armB) * (H.armLen * 0.5), y: ey + Math.cos(armB) * (H.armLen * 0.5) + 1.8, a: armB };
}

// ---------- head ----------
function drawHead(ctx: CanvasRenderingContext2D, ink: string, head: Look | null, seed: number, view: string, self: boolean, tier = 0) {
  const hy = H.head, r = H.headR;
  const covered = head?.kind === 'helm' || head?.kind === 'hood';
  // neck
  inkPath(ctx, [[-2.8, H.shoulder + 1], [2.8, H.shoulder + 1], [2.4, hy + r - 2], [-2.4, hy + r - 2]],
    { color: SKIN_LINE, width: HW.detail, seed: seed + 20, amp: 0.4, close: true, fill: '#e9c9a2' });
  hatch(ctx, -2.6, hy + r - 3, 5.2, 3, { gap: 2.2, angle: -1.2, alpha: 0.3, seed: seed + 21 });

  // The skull is one clean shape with a solid fill. At this size, ears, eyebrows and hair strands
  // turn into scribble; what reads is the silhouette, two eyes and a mouth.
  const skull: [number, number][] = [
    [-r * 0.94, hy - 1], [-r * 0.8, hy - r * 0.82], [0, hy - r * 1.08], [r * 0.8, hy - r * 0.82], [r * 0.94, hy - 1],
    [r * 0.84, hy + r * 0.58], [r * 0.4, hy + r * 1.04], [0, hy + r * 1.14], [-r * 0.4, hy + r * 1.04], [-r * 0.84, hy + r * 0.58],
  ];
  inkPath(ctx, skull, { color: SKIN_LINE, width: HW.silhouette * 0.78, seed: seed + 22, amp: 0.4, close: true, fill: '#f6dcbc' });
  // the light side of the face
  ctx.save();
  ctx.beginPath(); skull.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip();
  ctx.globalAlpha = 0.5; ctx.fillStyle = '#fff3e2'; ctx.fillRect(-r, hy - r * 1.2, r * 0.9, r * 2.4);
  ctx.globalAlpha = 0.16; ctx.fillStyle = '#9a6f46'; ctx.fillRect(r * 0.2, hy - r * 1.2, r, r * 2.4);
  ctx.restore();

  if (view === 'back') {
    inkPath(ctx, [[-r * 0.95, hy + 1], [-r * 0.9, hy - r * 0.85], [0, hy - r * 1.12], [r * 0.9, hy - r * 0.85], [r * 0.95, hy + 1], [r * 0.6, hy + r * 0.5], [-r * 0.6, hy + r * 0.5]],
      { color: ink, width: HW.edge, seed: seed + 24, amp: 0.45, close: true, fill: ink + 'cc' });
  } else if (!covered) {
    // hair: one shape, no strands
    inkPath(ctx, [[-r * 0.96, hy - 1], [-r * 0.86, hy - r * 0.95], [0, hy - r * 1.14], [r * 0.86, hy - r * 0.95], [r * 0.96, hy - 1],
      [r * 0.55, hy - r * 0.38], [0, hy - r * 0.6], [-r * 0.5, hy - r * 0.26], [-r * 0.8, hy - r * 0.5]],
      { color: ink, width: HW.edge, seed: seed + 34, amp: 0.4, close: true, fill: ink + 'd0' });
  }

  if (view !== 'back') {
    const dx = view === 'side' ? 2.6 : 0;
    ctx.fillStyle = INK;
    if (view === 'side') {
      ctx.beginPath(); ctx.ellipse(dx + 2.4, hy - 0.6, 1.5, 1.9, 0, 0, 7); ctx.fill();
      inkPath(ctx, [[r * 0.74, hy - 0.5], [r * 1.1, hy + 1.8], [r * 0.74, hy + 2.6]], { color: SKIN_LINE, width: HW.shade, seed: seed + 26, amp: 0.3, alpha: 0.8 });
      inkLine(ctx, dx + 1.6, hy + 4.6, dx + 4.2, hy + 4.4, { color: INK, width: HW.detail, amp: 0.3, seed: seed + 27 });
    } else {
      ctx.beginPath(); ctx.ellipse(-3.5, hy - 0.6, 1.6, 2, 0, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.ellipse(3.5, hy - 0.6, 1.6, 2, 0, 0, 7); ctx.fill();
      // a spark of light in each eye is what makes a face look alive rather than stamped
      ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.arc(-4, hy - 1.4, 0.55, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.arc(3, hy - 1.4, 0.55, 0, 7); ctx.fill();
      inkLine(ctx, -1.9, hy + 4.4, 1.9, hy + 4.4, { color: INK, width: HW.detail, amp: 0.3, seed: seed + 33 });
    }
  }

  if (!head) return;
  drawHeadGear(ctx, head, seed, hy, r, tier);
  void self;
}

function drawHeadGear(ctx: CanvasRenderingContext2D, head: Look, seed: number, hy: number, r: number, tier = 1) {
  const c = head.color ?? INK, ac = head.accent ?? c;
  void fillOf;
  switch (head.kind) {
    case 'cap': {
      inkPath(ctx, [[-r - 2.5, hy - r + 2], [0, hy - r - 9.5], [r + 2.5, hy - r + 2]], { color: c, width: W.edge, seed: seed + 28, amp: 0.5, close: true, fill: 'rgba(255,255,255,0.72)' });
      inkPath(ctx, [[-r - 3.5, hy - r + 2.4], [0, hy - r + 0.4], [r + 3.5, hy - r + 2.4]], { color: c, width: W.edge, seed: seed + 29, amp: 0.4 });
      inkLine(ctx, -r + 1.5, hy - r - 1.6, r - 1.5, hy - r - 1.6, { color: ac, width: W.shade, amp: 0.4, alpha: 0.8, seed: seed + 30 });
      inkLine(ctx, 0, hy - r - 9, -r * 0.3, hy - r - 4, { color: ac, width: W.shade, amp: 0.4, alpha: 0.6, seed: seed + 31 });
      break;
    }
    case 'hood': {
      // A hood is a ring of cloth with a hole in it, not a bag over the head: the fill has to miss
      // the opening or the face disappears under it.
      const peak = hy - r - 7;
      const outer: [number, number][] = [
        [-r - 4, hy + 8], [-r - 2.5, hy - r * 0.5], [-r * 0.5, peak + 2], [r * 0.2, peak], [r + 2.5, hy - r * 0.35], [r + 4, hy + 8], [r - 1, hy + 4.5], [-r + 1, hy + 4.5],
      ];
      const hole: [number, number][] = [
        [-r + 0.5, hy + 3.5], [-r * 0.68, hy - r * 0.8], [0, hy - r * 0.95], [r * 0.6, hy - r * 0.78], [r - 0.5, hy + 3.5], [r * 0.5, hy + 4], [-r * 0.5, hy + 4],
      ];
      ctx.save();
      ctx.beginPath();
      outer.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)));
      ctx.closePath();
      hole.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)));
      ctx.closePath();
      ctx.fillStyle = fillOf(head, tier); ctx.fill('evenodd');
      ctx.restore();
      // the shadow the brim throws over the brow
      ctx.save();
      ctx.beginPath(); hole.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py))); ctx.closePath(); ctx.clip();
      ctx.globalAlpha = 0.24; ctx.fillStyle = '#2c2c44';
      ctx.fillRect(-r, hy - r * 1.1, r * 2, r * 0.75);
      ctx.restore();
      inkPath(ctx, outer, { color: c, width: HW.silhouette * 0.85, seed: seed + 32, amp: 0.5, close: true });
      inkPath(ctx, hole, { color: c, width: HW.detail, seed: seed + 33, amp: 0.4, close: true, alpha: 0.9 });
      // folds down the cloth, and the cowl gathered at the neck
      for (const fx of [-r * 0.85, r * 0.7]) {
        inkPath(ctx, [[fx * 1.05, hy + 1], [fx * 1.15, hy + 5], [fx * 1.2, hy + 8]], { color: c, width: HW.shade, seed: seed + 60 + fx, amp: 0.4, alpha: 0.55 });
      }
      inkLine(ctx, -r - 2.5, hy + 6.5, r + 2.5, hy + 6.5, { color: ac, width: HW.detail, amp: 0.5, alpha: 0.8, seed: seed + 35 });
      break;
    }
    case 'helm': {
      // A proper helm: a dome that clears the skull, cheek guards, a nasal bar, and a crest that
      // gets taller and louder the better the helm is.
      const top = hy - r - 5.5 - tier * 0.9;
      inkPath(ctx, [[-r - 2, hy + 3], [-r - 2.2, hy - 3.5], [-r * 0.6, top + 1], [0, top], [r * 0.6, top + 1], [r + 2.2, hy - 3.5], [r + 2, hy + 3]],
        { color: c, width: W.contour, seed: seed + 35, amp: 0.45, close: true, fill: fillOf(head, tier) });
      // the light runs down the left of the dome
      ctx.save(); ctx.globalAlpha = 0.45; ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.ellipse(-r * 0.42, hy - 3.5, r * 0.34, r * 0.6, -0.3, 0, 7); ctx.fill(); ctx.restore();
      // brow band and nasal
      inkLine(ctx, -r - 2, hy - 3, r + 2, hy - 3, { color: ac, width: W.detail, amp: 0.4, seed: seed + 37 });
      inkPath(ctx, [[-1.6, hy - 3], [-1.6, hy + 6], [1.6, hy + 6], [1.6, hy - 3]],
        { color: c, width: W.detail, seed: seed + 36, amp: 0.3, close: true, fill: fillOf(head, tier) });
      // cheek guards on anything above the basic helm
      if (tier >= 2) for (const s2 of [-1, 1] as const) {
        inkPath(ctx, [[s2 * (r + 1.8), hy - 2.5], [s2 * (r + 1.4), hy + 5.5], [s2 * (r - 2), hy + 4.5], [s2 * (r - 2.6), hy - 2]],
          { color: c, width: W.detail, seed: seed + 44 + s2, amp: 0.35, close: true, fill: fillOf(head, tier) });
      }
      // rivets
      for (const s2 of [-1, 1]) { ctx.fillStyle = ac; ctx.beginPath(); ctx.arc(s2 * (r - 1), hy - 5, 1, 0, 7); ctx.fill(); }
      // the crest: a comb on a decent helm, a full plume on a relic
      if (tier >= 3) {
        const ch = 5 + tier * 1.8;
        inkPath(ctx, [[-r * 0.5, top + 1.5], [-1.5, top - ch], [1.5, top - ch], [r * 0.5, top + 1.5]],
          { color: ac, width: W.detail, seed: seed + 46, amp: 0.4, close: true, fill: 'rgba(200,48,47,0.5)' });
        if (tier >= 4) {
          for (let i = 0; i < 7; i++) {
            const f = i / 6;
            inkPath(ctx, [[-1 + f * 2, top - ch + 1], [-4 - f * 5, top - ch - 3 - f * 2], [-2 - f * 4, top - ch + 2 + f]],
              { color: ac, width: W.shade, seed: seed + 50 + i, amp: 0.5, alpha: 0.8 });
          }
        }
      }
      // wings on the very best helm
      if (tier >= 5) for (const s2 of [-1, 1] as const) {
        inkPath(ctx, [[s2 * (r - 1), hy - 4], [s2 * (r + 8), hy - 10], [s2 * (r + 9), hy - 4], [s2 * (r + 4), hy - 2]],
          { color: '#ffffff', width: W.detail, seed: seed + 60 + s2, amp: 0.4, close: true, fill: 'rgba(255,255,255,0.8)' });
        for (let i = 0; i < 3; i++) inkLine(ctx, s2 * (r + 1 + i), hy - 4, s2 * (r + 6 + i), hy - 8 + i, { color: ac, width: W.shade, amp: 0.3, alpha: 0.6, seed: seed + 70 + i });
      }
      hatch(ctx, 1.5, hy - r - 3, r, r + 3, { gap: 2.6, angle: -1.2, alpha: 0.2, seed: seed + 39 });
      break;
    }
    case 'crown': {
      // a circlet with points; the better it is, the taller the points and the more stones in it
      const y = hy - r - 1 - tier * 0.7;
      const pk = 4 + tier * 1.4;
      const pts: [number, number][] = [[-r - 1, y + 4]];
      const teeth = tier >= 4 ? 5 : 3;
      for (let i = 0; i < teeth; i++) {
        const f0 = i / teeth, f1 = (i + 0.5) / teeth, f2 = (i + 1) / teeth;
        const mid = i === Math.floor(teeth / 2) ? pk * 1.5 : pk;
        pts.push([-r - 1 + f0 * (2 * r + 2), y + 0.5], [-r - 1 + f1 * (2 * r + 2), y - mid], [-r - 1 + f2 * (2 * r + 2), y + 0.5]);
      }
      pts.push([r + 1, y + 4]);
      inkPath(ctx, pts, { color: c, width: W.edge, seed: seed + 40, amp: 0.4, close: true, fill: 'rgba(240,200,90,0.75)' });
      inkLine(ctx, -r - 1, y + 2.4, r + 1, y + 2.4, { color: ac, width: W.shade, amp: 0.3, alpha: 0.9, seed: seed + 41 });
      // stones set into the band
      const gems = tier >= 4 ? 3 : 1;
      for (let i = 0; i < gems; i++) {
        const gx = (i - (gems - 1) / 2) * (r * 0.8);
        ctx.fillStyle = i % 2 ? '#3d55a0' : ac;
        ctx.beginPath(); ctx.arc(gx, y + 1.8, 1.5, 0, 7); ctx.fill();
      }
      ctx.save(); ctx.globalAlpha = 0.5; ctx.fillStyle = '#ffffff';
      ctx.fillRect(-r + 1, y + 0.8, r * 0.5, 1.2); ctx.restore();
      break;
    }
  }
}

// ---------- shield ----------
function drawShield(ctx: CanvasRenderingContext2D, look: Look, seed: number, side: -1 | 1, small = false, tier = 1, step = 0) {
  const h = handPos(side, step, 0);
  const cx = h.x + side * 9, cy = h.y - 2.5;
  const s = (small ? 0.85 : 1) * (1 + Math.max(0, tier - 1) * 0.07);
  const c = look.color ?? INK, ac = look.accent ?? c;
  const face = tier >= 4 ? 'rgba(232,236,242,0.95)' : 'rgba(246,244,238,0.95)';
  ctx.save(); ctx.translate(cx, cy); ctx.scale(s, s);
  if (look.shape === 'round') {
    inkEllipse(ctx, 0, 0, 11, 11.5, { color: c, width: W.contour, seed, amp: 0.9, double: true, fill: face });
    inkEllipse(ctx, 0, 0, 7.5, 8, { color: ac, width: W.detail, seed: seed + 1, amp: 0.7, alpha: 0.85 });
    // the boss in the middle, with a highlight on it
    inkEllipse(ctx, 0, 0, 3.2, 3.4, { color: c, width: W.detail, seed: seed + 2, amp: 0.4, fill: 'rgba(200,205,214,0.95)' });
    ctx.save(); ctx.globalAlpha = 0.65; ctx.fillStyle = '#ffffff';
    ctx.beginPath(); ctx.arc(-1.1, -1.2, 1.1, 0, 7); ctx.fill(); ctx.restore();
    // rivets round the rim
    for (let i = 0; i < 8; i++) { const a = (i / 8) * 6.28; ctx.fillStyle = ac; ctx.beginPath(); ctx.arc(Math.cos(a) * 9.4, Math.sin(a) * 9.8, 0.85, 0, 7); ctx.fill(); }
  } else if (look.shape === 'kite') {
    inkPath(ctx, [[-9, -11], [9, -11], [8, 2], [0, 14], [-8, 2]], { color: c, width: W.contour, seed, amp: 0.9, close: true, double: true, fill: face });
    inkPath(ctx, [[-7, -9], [7, -9], [6.2, 1.5], [0, 11.5], [-6.2, 1.5]], { color: ac, width: W.shade, seed: seed + 1, amp: 0.6, close: true, alpha: 0.75 });
    inkLine(ctx, 0, -9.5, 0, 12, { color: ac, width: W.detail, amp: 0.6, seed: seed + 2, alpha: 0.9 });
    inkLine(ctx, -6.5, -4, 6.5, -4, { color: ac, width: W.detail, amp: 0.6, seed: seed + 3, alpha: 0.9 });
  } else {
    // a notebook page as a shield: ruled lines, a red margin, a torn corner
    inkPath(ctx, [[-9.5, -12], [9.5, -12], [9.5, 11], [-9.5, 11]], { color: c, width: W.contour, seed, amp: 0.8, close: true, double: true, fill: face });
    for (let i = -8; i <= 9; i += 3.4) inkLine(ctx, -8.5, i, 8.5, i, { color: '#bcd0ee', width: 0.9, amp: 0.5, alpha: 0.9, seed: seed + i + 40 });
    inkLine(ctx, -5.5, -11.5, -5.5, 10.5, { color: RED, width: 1.1, amp: 0.6, alpha: 0.85, seed: seed + 5 });
    for (let i = 0; i < 3; i++) { ctx.fillStyle = ac; ctx.beginPath(); ctx.arc(-7.6, -9 + i * 9, 0.9, 0, 7); ctx.fill(); }
  }
  if (look.crest === 'star') star(ctx, 0, 0, 5.5, ac, seed + 6);
  if (look.crest === 'blot') { ctx.fillStyle = ac; ctx.beginPath(); ctx.arc(0, 0, 3, 0, 7); ctx.fill(); }
  // the light hits the top left of the face
  ctx.save(); ctx.globalAlpha = 0.28; ctx.fillStyle = '#ffffff';
  ctx.beginPath(); ctx.ellipse(-3.5, -4.5, 4, 5, -0.4, 0, 7); ctx.fill(); ctx.restore();
  if (look.hatch) hatch(ctx, 1.5, -10, 8, 20, { gap: 4, angle: -1.2, alpha: 0.18, seed: seed + 7 });
  ctx.restore();
}

/** slung across the back: the rim shows past the shoulder */
function drawShieldOnBack(ctx: CanvasRenderingContext2D, look: Look, seed: number) {
  const c = look.color ?? INK, ac = look.accent ?? c;
  ctx.save();
  ctx.translate(-H.torsoW / 2 - 2, H.chest + 1);
  ctx.rotate(-0.25);
  ctx.scale(0.92, 0.92);
  if (look.shape === 'round') inkEllipse(ctx, 0, 0, 9.5, 10, { color: c, width: W.edge, seed, amp: 0.6, fill: 'rgba(255,255,255,0.5)' });
  else if (look.shape === 'kite') inkPath(ctx, [[-8, -9], [8, -9], [7, 3], [0, 12], [-7, 3]], { color: c, width: W.edge, seed, amp: 0.6, close: true, fill: 'rgba(255,255,255,0.5)' });
  else inkPath(ctx, [[-8, -10], [8, -10], [8, 10], [-8, 10]], { color: c, width: W.edge, seed, amp: 0.6, close: true, fill: 'rgba(255,255,255,0.6)' });
  inkLine(ctx, -6, -2, 6, -2, { color: ac, width: W.shade, amp: 0.3, alpha: 0.8, seed: seed + 1 });
  ctx.restore();
}

function star(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, color: string, seed: number) {
  const pts: [number, number][] = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? r * 0.45 : r;
    pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
  }
  inkPath(ctx, pts, { color, width: 1.2, seed, amp: 0.6, close: true, fill: 'rgba(216,163,74,0.45)' });
}

// ---------- weapons ----------
function drawWeapon(ctx: CanvasRenderingContext2D, look: Look, seed: number, swing: number, view: string, tier = 1, idle = false) {
  if (look.kind === 'bow') return drawBow(ctx, look, seed, swing);
  const h = handPos(1, idle ? 0.25 : 0, swing);
  const c = look.color ?? PENCIL, ac = look.accent ?? c;
  const len = look.len ?? 24;
  // a sword swings through a wide arc, a staff barely lifts
  const arc = look.kind === 'staff' ? [0.3, 0.95] : [0.5, 1.85];
  // at rest the blade is carried out and down, not hanging straight: it reads as ready
  const ang = (idle && swing === 0 ? (look.kind === 'staff' ? 0.06 : 0.22) : arc[0]!) + (arc[1]! - arc[0]!) * ease(swing);
  ctx.save();
  ctx.translate(h.x, h.y);
  ctx.rotate(ang);
  switch (look.kind) {
    case 'sword':
    case 'dagger': {
      // A blade is the thing a player looks at. It gets longer, wider and better dressed with
      // every tier: a real crossguard, a fuller, a pommel stone, and runes on a relic.
      const bw = 2.4 + tier * 0.35;                       // half the blade width
      const guard = 5 + tier * 1.1;
      inkPath(ctx, [[-1.8, 2], [1.8, 2], [1.5, -7], [-1.5, -7]], { color: '#4a3a24', width: W.detail, seed, amp: 0.4, close: true, fill: 'rgba(74,58,36,0.7)' });
      for (const gy of [-1.5, -3.5, -5.5]) inkLine(ctx, -1.6, gy, 1.6, gy, { color: '#2e2418', width: W.shade, amp: 0.25, alpha: 0.7, seed: seed + gy });
      // pommel, with a stone in it above common
      inkEllipse(ctx, 0, 2.6, 2.4, 2.4, { color: ac, width: W.detail, seed: seed + 9, amp: 0.3, fill: 'rgba(216,163,74,0.65)' });
      if (tier >= 3) { ctx.fillStyle = '#c8302f'; ctx.beginPath(); ctx.arc(0, 2.6, 1.1, 0, 7); ctx.fill(); }
      // crossguard: quillons that sweep towards the blade on better swords
      if (tier >= 3) {
        inkPath(ctx, [[-guard, -5.5], [-guard * 0.6, -8.5], [guard * 0.6, -8.5], [guard, -5.5], [guard * 0.7, -5], [-guard * 0.7, -5]],
          { color: ac, width: W.detail, seed: seed + 1, amp: 0.4, close: true, fill: 'rgba(216,163,74,0.6)' });
      } else {
        inkPath(ctx, [[-guard, -6.6], [guard, -6.6], [guard * 0.8, -5], [-guard * 0.8, -5]],
          { color: ac, width: W.detail, seed: seed + 1, amp: 0.4, close: true, fill: 'rgba(216,163,74,0.55)' });
      }
      // the blade
      inkPath(ctx, [[-bw, -7], [bw, -7], [bw * 0.85, -len - 3], [0, -len - 9], [-bw * 0.85, -len - 3]],
        { color: c, width: W.edge, seed: seed + 2, amp: 0.6, close: true, fill: 'rgba(255,255,255,0.72)' });
      // the light down one side of the steel, and the fuller down the middle
      ctx.save(); ctx.globalAlpha = 0.5; ctx.fillStyle = '#ffffff';
      ctx.fillRect(-bw * 0.8, -len - 2, bw * 0.55, len - 5); ctx.restore();
      inkLine(ctx, 0, -9, 0, -len - 3, { color: c, width: W.shade, amp: 0.4, alpha: 0.6, seed: seed + 3 });
      if (tier >= 4) {
        for (let i = 0; i < 4; i++) {
          const ry = -12 - i * (len / 5);
          inkLine(ctx, -1, ry, 1, ry - 1.5, { color: ac, width: W.shade, amp: 0.3, alpha: 0.85, seed: seed + 20 + i });
        }
      }
      if (look.hatch) hatch(ctx, bw * 0.15, -len - 4, bw, len, { gap: 3, angle: -1.3, alpha: 0.2, seed: seed + 4 });
      break;
    }
    case 'axe': {
      const hw = 6 + tier * 1.6;
      inkPath(ctx, [[-1.8, 3], [1.8, 3], [1.5, -len - 2], [-1.5, -len - 2]], { color: '#4a3a24', width: W.detail, seed, amp: 0.5, close: true, fill: 'rgba(74,58,36,0.75)' });
      // the head, with a bearded edge and a back spike on the good ones
      inkPath(ctx, [[0, -len + 3], [hw, -len - 4], [hw + 1.5, -len + 5], [0, -len + 11]],
        { color: c, width: W.edge, seed: seed + 1, amp: 0.8, close: true, fill: 'rgba(255,255,255,0.68)' });
      inkLine(ctx, 1.5, -len + 4, hw, -len, { color: ac, width: W.detail, amp: 0.5, alpha: 0.9, seed: seed + 2 });
      if (tier >= 3) inkPath(ctx, [[0, -len + 4], [-4.5, -len + 1], [-4, -len + 7], [0, -len + 8]],
        { color: c, width: W.detail, seed: seed + 5, amp: 0.5, close: true, fill: 'rgba(255,255,255,0.55)' });
      for (const by of [-len + 2, -len + 9]) inkLine(ctx, -2, by, 2, by, { color: ac, width: W.shade, amp: 0.3, alpha: 0.8, seed: seed + by });
      if (look.hatch) hatch(ctx, 2, -len - 2, hw, 12, { gap: 3, angle: -1.1, alpha: 0.22, seed: seed + 3 });
      break;
    }
    case 'staff': {
      // A staff is the tallest thing a character carries: a long shaft with a wrapped grip, claws
      // holding a stone at the top, and the stone lighting the air around it.
      pencil(ctx, [[-2.1, 6], [2.1, 6], [1.5, -len], [-1.5, -len]],
        { color: c, width: HW.silhouette * 0.7, seed, amp: 0.6, fill: C_WOOD, fillAlpha: 0.85 });
      // the grain, and the leather wrap where the hand goes
      for (let i = 0; i < 5; i++) inkLine(ctx, -1.7, -2 - i * (len / 7), 1.7, -3 - i * (len / 7), { color: '#2e2418', width: HW.shade, amp: 0.3, alpha: 0.5, seed: seed + i });
      inkPath(ctx, [[-2.4, 3], [2.4, 3], [2.2, -7], [-2.2, -7]],
        { color: '#4a3a24', width: HW.detail, seed: seed + 20, amp: 0.4, close: true, fill: 'rgba(90,67,38,0.85)' });
      for (let i = 0; i < 4; i++) inkLine(ctx, -2.3, 1.6 - i * 2.4, 2.3, 0.6 - i * 2.4, { color: '#2e2418', width: HW.shade, amp: 0.25, alpha: 0.7, seed: seed + 30 + i });
      // a band of metal partway up
      inkPath(ctx, [[-2.1, -len * 0.55], [2.1, -len * 0.55], [1.9, -len * 0.62], [-1.9, -len * 0.62]],
        { color: ac, width: HW.shade, seed: seed + 21, amp: 0.3, close: true, fill: ac + 'aa' });
      // the claws that hold the stone
      const claws = 3 + Math.min(2, Math.max(0, tier - 2));
      for (let i = 0; i < claws; i++) {
        const f = claws === 1 ? 0 : i / (claws - 1) - 0.5;
        inkPath(ctx, [[f * 5.5, -len + 1], [f * 10, -len - 8], [f * 6, -len - 14]],
          { color: ac, width: HW.detail, seed: seed + 10 + i, amp: 0.4, alpha: 0.95 });
      }
      // the stone, with the light inside it
      const orb = 5 + tier * 0.7;
      inkEllipse(ctx, 0, -len - 10, orb, orb * 1.08,
        { color: ac, width: HW.detail, seed: seed + 1, amp: 0.4, fill: 'rgba(61,85,160,0.72)' });
      ctx.save(); ctx.globalAlpha = 0.85; ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.ellipse(-orb * 0.32, -len - 10 - orb * 0.35, orb * 0.3, orb * 0.22, -0.5, 0, 7); ctx.fill(); ctx.restore();
      // the air around it
      inkEllipse(ctx, 0, -len - 10, orb * 1.5, orb * 1.55, { color: ac, width: HW.shade, amp: 0.9, alpha: 0.35, seed: seed + 2, overshoot: 1.1 });
      for (let i = 0; i < (tier >= 4 ? 8 : 5); i++) {
        const a2 = (i / (tier >= 4 ? 8 : 5)) * 6.28 + 0.3;
        inkLine(ctx, Math.cos(a2) * orb * 1.8, -len - 10 + Math.sin(a2) * orb * 1.8,
          Math.cos(a2) * orb * 2.5, -len - 10 + Math.sin(a2) * orb * 2.5,
          { color: ac, width: HW.shade, amp: 0.3, alpha: 0.45, seed: seed + 30 + i });
      }
      // motes drifting off it on a relic staff
      if (tier >= 4) for (let i = 0; i < 3; i++) {
        ctx.fillStyle = ac; ctx.globalAlpha = 0.5;
        ctx.beginPath(); ctx.arc(Math.cos(i * 2.1) * orb * 2.2, -len - 16 - i * 4, 1.3, 0, 7); ctx.fill();
        ctx.globalAlpha = 1;
      }
      break;
    }
  }
  ctx.restore();
  // the swoosh: a stroke through the air along the path the tip has just taken
  if (swing > 0.18 && swing < 0.92 && look.kind !== 'staff') {
    const from = Math.max(0, swing - 0.3);
    const arc2 = (v: number) => arc[0]! + (arc[1]! - arc[0]!) * ease(v);
    const pts: [number, number][] = [];
    for (let i = 0; i <= 6; i++) {
      const a2 = arc2(from + ((swing - from) * i) / 6);
      const rr = len + 1;
      pts.push([h.x + Math.sin(a2) * rr, h.y - Math.cos(a2) * rr]);
    }
    inkPath(ctx, pts, { color: c, width: W.edge, seed: seed + 41, amp: 0.8, alpha: 0.4 });
    const inner = pts.map(([px, py]) => [h.x + (px - h.x) * 0.76, h.y + (py - h.y) * 0.76] as [number, number]);
    inkPath(ctx, inner, { color: c, width: W.shade, seed: seed + 42, amp: 0.7, alpha: 0.22 });
  }
  void view;
}
const ease = (k: number) => (k <= 0 ? 0 : k >= 1 ? 1 : 1 - Math.pow(1 - k, 2.2));

/** Both hands work a bow: one arm holds it out, the other pulls the string back to the chin. */
function drawBowPose(ctx: CanvasRenderingContext2D, ink: string, chest: Look | null, look: Look, swing: number, tier = 1) {
  const c = chest?.color ?? ink;
  const mat = fillOf(chest, tier);
  const k = swing >= 0.95 ? 0 : swing;                   // the last frame is the release
  const sh = H.shoulder + 2;
  const half = (look.len ?? 30) / 2 + 6;
  const bowX = -H.torsoW / 2 - 15, bowY = H.chest + 1;   // held out clear of the body
  const bc = look.color ?? PENCIL, ac = look.accent ?? bc;

  // the bow limbs, belly towards the target on the left, string on the archer's side
  ctx.save(); ctx.translate(bowX, bowY); ctx.rotate(0.05);
  inkPath(ctx, [[0, -half], [-7, -half * 0.55], [-8.5, 0], [-7, half * 0.55], [0, half]],
    { color: bc, width: HW.silhouette, seed: 66, amp: 0.5 });
  for (const ty of [-half, half]) inkLine(ctx, 0, ty, -1.5, ty * 0.92, { color: ac, width: HW.detail, amp: 0.3, seed: 72 + ty });
  ctx.restore();

  // the drawing hand comes back towards the chin as the string is pulled
  const pull = 13 + k * 9;
  const handX = bowX + pull, handY = bowY - 2 - k * 2;

  // front arm: shoulder to the grip, straight
  inkPath(ctx, [[-H.torsoW / 2 + 1, sh - 2.4], [-H.torsoW / 2 + 1, sh + 2.6], [bowX + 3, bowY + 3], [bowX + 3, bowY - 1.6]],
    { color: c, width: HW.silhouette * 0.8, seed: 61, amp: 0.45, close: true, fill: mat });
  // drawing arm: shoulder, elbow up and back, then the forearm to the string
  const elbowX = H.torsoW / 2 - 3 + k * 6, elbowY = sh + 1 - k * 3;
  inkPath(ctx, [[H.torsoW / 2 - 1, sh - 2.4], [H.torsoW / 2 - 1, sh + 2.6], [elbowX, elbowY + 2.6], [elbowX, elbowY - 1.6]],
    { color: c, width: HW.silhouette * 0.8, seed: 63, amp: 0.45, close: true, fill: mat });
  inkPath(ctx, [[elbowX, elbowY - 1.6], [elbowX, elbowY + 2.6], [handX, handY + 2], [handX, handY - 1.8]],
    { color: c, width: HW.silhouette * 0.75, seed: 64, amp: 0.45, close: true, fill: mat });

  // the string, bent back to the drawing hand
  ctx.save(); ctx.translate(bowX, bowY); ctx.rotate(0.05);
  inkPath(ctx, [[0, -half + 1], [handX - bowX, handY - bowY], [0, half - 1]], { color: ac, width: HW.shade, seed: 67, amp: 0.15, alpha: 0.9 });
  // The arrow runs from the string, through the bow, and out the far side. It points away from the
  // archer — the first version had it pointing into his own chest.
  if (swing < 0.95) {
    const ay = handY - bowY, nock = handX - bowX;
    inkLine(ctx, nock, ay, -24, ay - 1, { color: PENCIL, width: HW.detail + 0.2, amp: 0.2, seed: 68 });
    inkPath(ctx, [[-24, ay - 1], [-18.5, ay - 3.6], [-19.5, ay - 1], [-18.5, ay + 1.6]],
      { color: PENCIL, width: HW.shade, seed: 69, amp: 0.2, close: true, fill: 'rgba(51,51,47,0.75)' });
    for (const fy of [-1, 1]) inkLine(ctx, nock - 0.5, ay + fy * 0.6, nock - 4, ay + fy * 3, { color: RED, width: HW.shade, amp: 0.2, alpha: 0.85, seed: 70 + fy });
  }
  ctx.restore();

  // hands last, so they sit on top of the string and the grip
  drawHand(ctx, bowX + 3, bowY + 1, chest, tier, 62);
  drawHand(ctx, handX, handY, chest, tier, 65);
}

/** a hand, or the gauntlet over it once the armour is worth one */
function drawHand(ctx: CanvasRenderingContext2D, x: number, y: number, chest: Look | null, tier: number, seed: number) {
  const plated = chest?.kind === 'plate' || tier >= 3;
  if (plated) {
    const ac = chest?.accent ?? '#8a8a8a';
    inkPath(ctx, [[x - 2.8, y - 2.6], [x + 2.8, y - 2.6], [x + 3.1, y + 2.2], [x, y + 3.4], [x - 3.1, y + 2.2]],
      { color: chest?.color ?? PENCIL, width: HW.detail, seed, amp: 0.35, close: true, fill: fillOf(chest, tier) });
    for (let i = 0; i < 2; i++) inkLine(ctx, x - 2.4, y - 1 + i * 1.8, x + 2.4, y - 1.3 + i * 1.8, { color: ac, width: HW.shade, amp: 0.25, alpha: 0.8, seed: seed + i });
  } else {
    inkEllipse(ctx, x, y, 2.8, 2.9, { color: SKIN_LINE, width: HW.detail, seed, amp: 0.35, fill: '#f0d0aa' });
    inkLine(ctx, x + 1.6, y - 1.4, x + 2.6, y - 0.2, { color: SKIN_LINE, width: HW.shade, amp: 0.25, alpha: 0.8, seed: seed + 3 });
  }
}

/** A bow is held out at arm's length, upright, and only the string moves. */
function drawBow(ctx: CanvasRenderingContext2D, look: Look, seed: number, swing: number) {
  const c = look.color ?? PENCIL, ac = look.accent ?? c;
  const half = (look.len ?? 30) / 2 + 4;
  ctx.save();
  ctx.translate(H.torsoW / 2 + 9, H.chest + 3);
  ctx.rotate(-0.12 + swing * 0.1);
  inkPath(ctx, [[0, -half], [6.5, -half * 0.55], [8, 0], [6.5, half * 0.55], [0, half]], { color: c, width: 2.2, seed, amp: 0.6, double: true });
  const pull = -swing * 6;
  inkPath(ctx, [[0, -half + 1], [pull, 0], [0, half - 1]], { color: ac, width: 1, seed: seed + 1, amp: 0.25, alpha: 0.9 });
  if (swing > 0.12) {
    inkLine(ctx, pull - 1, 0, 15, 0, { color: PENCIL, width: 1.5, amp: 0.25, seed: seed + 2 });
    inkLine(ctx, 15, 0, 11, -3, { color: PENCIL, width: 1.2, amp: 0.25, seed: seed + 3 });
    inkLine(ctx, 15, 0, 11, 3, { color: PENCIL, width: 1.2, amp: 0.25, seed: seed + 4 });
  }
  ctx.restore();
}

// ---------- cape ----------
function drawCape(ctx: CanvasRenderingContext2D, look: Look, seed: number, step: number, view: string, tier = 1) {
  const c = look.color ?? RED, ac = look.accent ?? c;
  const sway = step * 4.5;
  const grand = 1 + Math.max(0, tier - 1) * 0.16;            // a relic cape sweeps
  const w = (view === 'side' ? 9 : 13.5) * grand;
  const hem = -11 - (tier >= 4 ? 3 : 0);
  // the cloth itself, with a scalloped hem so it does not read as a bib
  const pts: [number, number][] = [[-w + 2, H.shoulder - 2], [w - 2, H.shoulder - 2], [w + 2 + sway, H.waist], [w - 1 + sway, hem]];
  const teeth = 5;
  for (let i = teeth; i >= 0; i--) {
    const f = i / teeth;
    const x = -w + 3 + sway + f * (2 * w - 4);
    pts.push([x, hem + (i % 2 ? 2.6 : 0)]);
  }
  pts.push([-w - 2 + sway, H.waist]);
  inkPath(ctx, pts, { color: c, width: W.edge, seed, amp: 0.7, close: true, fill: 'rgba(194,42,42,0.18)', alpha: 0.85 });
  // folds, more of them on a better cape
  const folds = 2 + Math.min(3, tier);
  for (let i = 0; i < folds; i++) {
    const fx = (-w + 4) + ((2 * w - 8) * (i + 0.5)) / folds;
    inkLine(ctx, fx * 0.45, H.chest, fx * 0.9 + sway * 0.7, hem + 1, { color: ac, width: W.shade, amp: 0.7, alpha: 0.45, seed: seed + 50 + i });
  }
  // the clasp that holds it at the throat, and a chain between the shoulders
  if (tier >= 2) {
    inkLine(ctx, -5.5, H.shoulder - 1.5, 5.5, H.shoulder - 1.5, { color: ac, width: W.detail, amp: 0.4, alpha: 0.9, seed: seed + 60 });
    for (const sx of [-5.5, 5.5]) {
      ctx.fillStyle = ac; ctx.beginPath(); ctx.arc(sx, H.shoulder - 1.5, tier >= 4 ? 2 : 1.5, 0, 7); ctx.fill();
    }
  }
}
